import { useTheme } from '@components/theme';
import { aadConfig, describeAuthError, psa } from '@services/aad';
import {
  getAccountById,
  getAccounts,
  getAccountUsers,
  getByLinks,
  getLaborRates,
  getMe,
  getUsers,
} from '@services/api';
import { getSubscription, getSubscriptionLicense } from '@services/api/subscription';
import { makeDashboardPath } from '@services/dom';
import {
  type Account,
  AccountType,
  type AccountWithUsers,
  type LaborRate,
  type LaborRateHistory,
  type Office,
  PRIMARY_PROVIDER_ID,
  ResourceUserRole,
  type User,
} from '@services/dto/account';
import type { Subscription } from '@services/dto/subscription';
import { isClientFeeDecider } from '@services/feeRights';
import { PERSONA_REGISTRY, resolvePersona } from '@services/persona/registry';
import { isProjectProvider, type ProjectLike } from '@services/projectProvider';
import { resolveProjectRights } from '@services/projectRights';
import { isProjectScopedAnywhere, isProjectScopedOn, isProjectScopedRefusal } from '@services/projectScopedGuest';
import { sameContent, sameId } from '@services/state/identity';
import { useAppDispatch, useAppSelector } from '@services/state/store';
import axios from 'axios';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { currentAccountFor } from './accountInPath';
import { accountActions } from './slice';
import { type Rights, UserType } from './types';
import { visibleAccounts } from './visibleAccounts';

function useMe<T extends User = User>(): [
  me: T | undefined,
  refresh: () => Promise<T | undefined>,
  setMe: (user: T) => void,
  /** True when the last load failed for a reason that is not a bad session. Added at the end of
      the tuple so the many callers that take only the first one or two are unaffected. */
  meError: boolean,
] {
  const state = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();

  const { setTheme } = useTheme();

  const setMe = React.useCallback((user: T | undefined) => dispatch(accountActions.setMe(user)), []);

  const refresh = React.useCallback(
    async (abort?: AbortController) => {
      if (state.loadingMe) return state.me as T;

      /*
       * No account yet is not a failure and not this hook's problem.
       *
       * It used to call login() here, which clears the MSAL cache and redirects. Run while MSAL
       * was still resolving a redirect, that deleted the code_verifier for the sign-in in flight
       * and started another one. useMsalAuthentication in Routes owns interaction - it is the one
       * place that knows whether a redirect is already under way - so this returns and lets it.
       */
      const activeAccount = psa().getActiveAccount() || psa().getAllAccounts()[0];
      if (!activeAccount) {
        setMe(undefined);
        return undefined;
      }

      dispatch(accountActions.setLoadingMe(true));

      try {
        const result = await psa().acquireTokenSilent({
          account: activeAccount,
          scopes: aadConfig().scopes,
        });

        const { accessToken, account } = result;

        psa().setActiveAccount(account);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        const me = await getMe({ abort });
        if (!me) {
          setMe(undefined);
          return undefined;
        }

        const actSetting = me.accountSettings?.find((x) => x.accountId === state.currentAccount?.id);
        if (actSetting) {
          me.hourlyRate = actSetting.hourlyRate ?? me.hourlyRate;
          me.jobTitle = actSetting.jobTitle ?? me.jobTitle;
          me.availability = actSetting.availability ?? me.availability;
          me.expertRole = actSetting.expertRole ?? me.expertRole;
          me.expertType = actSetting.expertType ?? me.expertType;
        }

        setTheme(me.theme || localStorage.getItem('theme') || ('light' as never));

        setMe({
          ...me,
          pictureUrl: me.pictureUrl ? `${me.pictureUrl}?_=${Date.now()}` : undefined,
        } as T);
      } catch (err) {
        if (abort?.signal.aborted) return;

        /*
         * A failing /users/me is not proof of a bad session.
         *
         * Signing the person out and bouncing them to the marketing site is right for an auth
         * failure and wrong for everything else: a 409 or a 500 from our own bootstrap would
         * throw away a perfectly good session and send them away from the product. Only the
         * statuses that actually mean "this token is no good" clear the cache now. The rest are
         * surfaced, so the screen can offer a retry instead of spinning.
         */
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          psa().clearCache().finally();
          location.href = `${process.env.CC_SITE}`;
          return;
        }

        // Named, not just flagged. A token that cannot be acquired for the API scope while the
        // session itself is fine used to be completely silent - it redirected rather than
        // reported - and silence is what made this take a day to find.
        dispatch(accountActions.setMeError(status ? `HTTP ${status} from /users/me` : describeAuthError(err)));
      } finally {
        /*
         * Always. Every early exit above used to leave this true, and the guard at the top of
         * refresh turns that into a permanent one: loading forever, and no retry can run because
         * the first thing a retry does is see the flag and return.
         */
        dispatch(accountActions.setLoadingMe(false));
      }
    },
    [state.loadingMe, state.me],
  );

  React.useEffect(() => {
    // Not while an error is showing: the screen offers a retry, and re-running here on every
    // render would hammer a service that has already said no.
    if (state.me || state.meError) return;

    const abort = new AbortController();
    refresh(abort).finally();

    return () => {
      abort.abort();
    };
  }, [state.me, state.meError]);

  return [state.me as T, refresh, setMe, !!state.meError];
}

export default useMe;

/**
 * The soft load, shared across every mounting consumer.
 *
 * `useUserAccounts` is reached by `useCurrentUserAccount`, which is reached by
 * almost every hook on a page, so a route mounts a dozen or more instances at
 * once. Each one ran its own soft refresh against a `state.accounts` that was
 * still undefined in its closure, so each one called `getAccounts`, mapped its
 * own array and dispatched it. The API dedupe collapsed the HTTP calls into one
 * response, but every caller still built a DIFFERENT array from it and wrote
 * that to the store - one new list reference per instance, each re-running
 * every effect keyed on the account.
 *
 * Holding the in-flight load here makes the whole read-map-dispatch a single
 * operation, so the list lands in the store exactly once.
 */
let softAccountLoad: Promise<void> | undefined;

export const useUserAccounts = (): [accounts: Account[] | undefined, refresh: (soft?: boolean) => Promise<void>] => {
  const [me] = useMe();

  const state = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();

  const load = React.useCallback(async () => {
    try {
      const accounts = (await getAccounts()).map((a) => ({
        ...a,
        logoUrl: a.logoUrl ? `${a.logoUrl}?_=${Date.now()}` : undefined,
      }));

      // Stored as the server gave it. Which of these the caller may see is decided on the way
      // out, by visibleAccounts, because that answer depends on `me` - see the note there.
      dispatch(accountActions.setAccounts(accounts));
    } catch (err) {
      console.error(err);
      dispatch(accountActions.setAccounts([]));
    }
  }, []);

  const refresh = React.useCallback(
    async (soft?: boolean) => {
      if (!soft) return load();

      if (state.accounts) return;
      if (softAccountLoad) return softAccountLoad;

      softAccountLoad = load().finally(() => {
        softAccountLoad = undefined;
      });
      return softAccountLoad;
    },
    [state.accounts, load],
  );

  React.useEffect(() => {
    refresh(true).finally();
  }, [me]);

  // Memoised so every instance of this hook keeps one array for its own lifetime. A dozen of them
  // mount at once and each feeds its reference to effects keyed on the account list; handing them
  // a fresh array per render is how this file's account churn started last time.
  const accounts = React.useMemo(() => visibleAccounts(state.accounts, me?.id), [state.accounts, me?.id]);

  return [accounts, refresh];
};

export const useCurrentUserAccount = (
  noRedirect?: boolean,
): [Account | undefined, (account?: Account | string) => void] => {
  const [accounts] = useUserAccounts();

  const state = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();

  const navigation = useNavigate();

  React.useEffect(() => {
    if (!accounts) return;
    // The URL's account first, then the one last used - see currentAccountFor. Reading the route
    // is the fix for a provider-account URL landing on the client's dashboard root.
    //
    // Deps stay [accounts] deliberately. This is the mount-time question "which account is this
    // page about", and switching accounts inside the app goes through setCurrentCb, which sets both
    // the store and localStorage. Re-running on every navigation would put the account reference
    // back in play on each route change, and the churn that causes is what the notes above and in
    // useUserAccounts are scars from.
    const a = currentAccountFor(accounts, window.location.pathname, localStorage.getItem('currentAccount'));

    // Only when it actually changes. Every instance of this hook runs this
    // effect, and writing back the account that is already current replaced the
    // reference in the store for no reason - which re-ran every effect keyed on
    // the account object, right down to the project-count aggregates.
    //
    // Same id is not the same account, though. Claiming an unclaimed account rewrites the record
    // in place - Deferred becomes Ready, the claimant becomes its owner - and keeps the id, so an
    // id-only comparison reads the claimed account as "no change" and leaves the store holding the
    // Deferred copy. That is invisible until something renders off the status, and then it is the
    // just-claimed customer still being offered the claim. Status is the one field that moves this
    // way, so it is the one checked: narrow enough not to put the reference back in play on every
    // render, which is what the note above is a scar from.
    const restated = sameId(a, state.currentAccount) && a?.status !== state.currentAccount?.status;

    if (!sameId(a, state.currentAccount) || restated || (!a && state.currentAccount)) {
      dispatch(accountActions.setCurrentAccount(a));
    }
    if (noRedirect) return;

    if (a) {
      localStorage.setItem('currentAccount', a.id);

      if (!window.location.pathname.startsWith('/dashboard')) return;

      if (a.id !== 'provider' && !window.location.pathname.startsWith(`/dashboard/${a.id}`))
        navigation(makeDashboardPath(a.id), { replace: true });
    }
  }, [accounts]);

  const setCurrentCb = React.useCallback(
    (account?: Account | string) => {
      if (!accounts || !account) {
        dispatch(accountActions.setCurrentAccount(undefined));
        localStorage.removeItem('currentAccount');
        return;
      }

      const id = typeof account === 'string' ? account : account.id;
      const a = accounts.find((x) => x.id === id);

      dispatch(accountActions.setCurrentAccount(a));
      if (a) localStorage.currentAccount = a.id;
    },
    [accounts],
  );

  return [state.currentAccount, setCurrentCb];
};

export const useCurrentAccount = (): [Account | undefined, () => Promise<void>] => {
  const [account] = useCurrentUserAccount();

  const state = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();

  const refresh = useCallback(async () => {
    if (!account?.id || account.id === state.currentFullAccount?.id) return;
    try {
      const fullAccount = await getAccountById(account.id);
      dispatch(accountActions.setCurrentFullAccount(fullAccount));
    } catch (err) {
      console.error(`Failed to fetch full account ${account.id} details:`, err);
    }
  }, [account?.id, state.currentFullAccount]);

  useEffect(() => {
    if (!account?.id || account.id === state.currentFullAccount?.id) return;
    refresh().finally();
  }, [account?.id, state.currentFullAccount?.id]);

  return [state.currentFullAccount, refresh];
};

export const useCurrentAccountWithUser = (
  projectId?: string,
): [AccountWithUsers | undefined, (noForce?: boolean) => void] => {
  const [account] = useCurrentUserAccount();
  const [me] = useMe();

  const state = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();

  const refreshCb = React.useCallback(
    async (noForce?: boolean) => {
      if (!account) return;
      if (noForce && state.currentAccountWithUsers?.id === account.id) return;

      // Not asked at all when we already know. The sibling hook below has had this guard since an
      // accepted firm first sat on a spinner; this one never got it, and three screens still call
      // the roster for somebody who can never be given it.
      /*
       * Two sources, and the second is why the refused calls repeated. isProjectScopedOn answers
       * only when the accounts payload carries isProjectScoped on this person's row; when it does
       * not, this guard passed and the roster was asked for again on every screen - three 403s per
       * visit to the projects list. The refusal already recorded is the surer answer, as
       * useIsProjectScopedGuest has always read it, so it counts here too.
       */
      if (isProjectScopedOn(account, me?.id) || state.projectScopedRefusedAccountId === account.id) {
        dispatch(accountActions.setProjectScopedRefused(account.id));
        return;
      }

      try {
        const accountWithUsers = await getAccountUsers(account.id, projectId);
        dispatch(accountActions.setCurrentAccountWithUsers(accountWithUsers));
      } catch (err) {
        /*
         * The account answering "you are only here for a project" is an answer, not a fault.
         *
         * It used to be logged and dropped, which left currentAccountWithUsers undefined for
         * good - and undefined reads as "still loading" to everything waiting on it. Recorded
         * now, so the bootstrap can stop waiting and show the workspace this person does have.
         *
         * Only that refusal. Anything else is still a failure and still surfaces.
         */
        if (isProjectScopedRefusal(err)) {
          dispatch(accountActions.setProjectScopedRefused(account.id));
          return;
        }

        console.error(`Error fetching current account ${account.id} users:`, err);
      }
    },
    [account, projectId, me?.id, state.currentAccountWithUsers?.id],
  );

  React.useEffect(() => {
    refreshCb(true);
  }, [account, projectId, refreshCb]);

  React.useEffect(() => {
    refreshCb();
  }, []);

  return [state.currentAccountWithUsers, refreshCb];
};

export const useAccountWithUser = (projectId?: string): [AccountWithUsers | undefined, () => Promise<void>] => {
  const state = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();

  const [account] = useCurrentUserAccount();

  const [me] = useMe();

  const refreshCb = React.useCallback(async () => {
    if (!account?.id || state.accountWithUsers?.id === account.id) return;

    /*
     * A project-scoped guest has no company roster to read.
     *
     * They are on the account to reach one project, and the server refuses them anything addressed
     * to the account - correctly. Asking anyway produced three 403s on every screen they opened,
     * and the answer never arriving is what left them on a spinner. Not asking is both faster and
     * the truth: there is no roster here that belongs to them.
     */
    if (isProjectScopedOn(account, me?.id) || state.projectScopedRefusedAccountId === account.id) {
      dispatch(accountActions.setAccountWithUsersError('project-scoped'));
      dispatch(accountActions.setProjectScopedRefused(account.id));
      return;
    }

    if (account.id === state.currentAccountWithUsers?.id) {
      dispatch(accountActions.setAccountWithUsers(state.currentAccountWithUsers));
      return;
    }

    try {
      const accountWithUsers = await getAccountUsers(account.id, projectId);
      dispatch(accountActions.setAccountWithUsers(accountWithUsers));
    } catch (err) {
      /*
       * The guard above answers from the account list, which carries isProjectScoped only when
       * the list's payload includes it. The server's refusal is the surer answer and arrives
       * here, so it is read as the same thing rather than as a fault.
       */
      if (isProjectScopedRefusal(err)) {
        dispatch(accountActions.setAccountWithUsersError('project-scoped'));
        dispatch(accountActions.setProjectScopedRefused(account.id));
        return;
      }

      /*
       * Refused, or unreachable. Either way it has answered, and recording that is the difference
       * between a page that says something and a page that spins. This used to log and return,
       * leaving every reader of the roster waiting for a value that was never coming.
       */
      console.error(`Error fetching account ${account.id} users:`, err);
      dispatch(accountActions.setAccountWithUsersError(describeAuthError(err)));
    }
  }, [account?.id, projectId, me, state.accountWithUsers?.id, state.currentAccountWithUsers?.id]);

  React.useEffect(() => {
    if (state.accountWithUsers?.id === account?.id) return;
    refreshCb().finally();
  }, [account?.id, projectId, state.accountWithUsers]);

  return [state.accountWithUsers, refreshCb];
};

/**
 * Whether the person is a guest on the account they are looking at.
 *
 * The server's own `isProjectScoped`, read off the accounts list - which is the one account call a
 * guest is allowed to make, so this can be answered without anything that would be refused. It
 * deliberately does not go through rights or the roster: both need a read a guest cannot have, so
 * asking them would make the lock depend on the very call the lock causes to fail.
 * @returns True when this account is only reachable for a project.
 */
export const useIsProjectScopedGuest = (): boolean => {
  const [account] = useCurrentUserAccount();
  const [accounts] = useUserAccounts();
  const [me] = useMe();
  const refused = useAppSelector((state) => state.account.projectScopedRefusedAccountId);

  /*
   * Two ways to know, and the second is the one that cannot be missed.
   *
   * The account list is the cheaper answer and needs no refused call to find out, so it is asked
   * first. But it only says somebody is a guest when the list's payload carries `isProjectScoped`
   * on their row - and when it does not, nothing else here knows, the screen waits for rights
   * that need a roster, the roster is refused, and the page spins with no one left to answer.
   *
   * So the refusal counts as well. ProjectScopedGuestFilter denies exactly when the person is a
   * project-scoped guest on that account, which makes its 403 a direct statement of the thing
   * this function exists to return.
   */
  if (account?.id) {
    return isProjectScopedOn(account, me?.id) || refused === account.id;
  }

  /*
   * No current account on this render, which is not the same as not being a guest.
   *
   * The current account is refreshed, and for the moments it is absent every caller asking this
   * got "no" - so the dashboard's gate re-closed, the screen went back to the spinner, and the
   * console showed it passing the gate once and then waiting again. Falling back to the list
   * answers the same question from the one read a guest is always allowed, so the answer stops
   * flickering between renders.
   */
  return isProjectScopedAnywhere(accounts, me?.id);
};

/**
 * Whether the roster question has been answered, one way or the other.
 *
 * True when the roster is here, and true when it could not be had. Anything that waits for a
 * roster waits on this rather than on the roster itself, so a refusal ends the wait instead of
 * extending it forever.
 * @returns Whether to stop waiting.
 */
/**
 * Rights on a project surface, for somebody who may only have the project.
 *
 * `useRights` is unchanged and still answers first. Where it answers nothing - a project-scoped
 * guest, whose company roster is deliberately never fetched - this fills in the two answers the
 * project itself supports, and no company answer at all. See resolveProjectRights.
 *
 * Use this on a project surface in place of `useRights`. Everywhere else, and on every
 * account-level screen, keep using `useRights`: a guest must not get firm chrome, the firm's
 * profile, its offices, its staff, the AI Hub, or the ability to create work on its behalf.
 * @param project - The project being viewed.
 * @param officeId - Passed through to `useRights`.
 * @returns The rights for this surface, or undefined when there are none.
 */
export const useProjectRights = (project?: ProjectLike | null, officeId?: string): Rights | undefined => {
  const rights = useRights(officeId, project?.id ?? undefined);
  const [me] = useMe();

  return React.useMemo(
    () =>
      resolveProjectRights(rights, {
        userId: me?.id,
        projectProvider: isProjectProvider(project, me?.id),
        accountId: rights?.accountId,
      }),
    [rights, me?.id, project],
  );
};

export const useAccountWithUserSettled = (): boolean => {
  const state = useAppSelector((s) => s.account);
  return !!state.accountWithUsers || !!state.accountWithUsersError;
};

export const useUserOffices = (): [offices: Office[] | undefined, refresh: () => void, inRefresh: boolean] => {
  const [me] = useMe();
  const [account] = useCurrentUserAccount();
  const [, refreshAccounts] = useUserAccounts();

  const state = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (!account || !me || account.id === state.officeAccountId) return;

    const au = account.users?.find((x) => x.id === me.id);

    if (!account.offices) {
      if (state.offices !== undefined) dispatch(accountActions.setOffices(undefined));
      return;
    }

    const counts = (office: Office) => {
      const o = { ...office };
      if (!account.counts) return o;
      o.counts = {
        projects: account.counts.officeProjects.find((x) => x.id === o.id)?.count || 0,
      };
      return o;
    };

    let accessibleOffices: Office[];
    if (au && (au.role === ResourceUserRole.Owner || au.role === ResourceUserRole.Administrator)) {
      // Account Owner or Admin sees all offices
      accessibleOffices = account.offices.map(counts);
    } else {
      // Others see only offices they are a member of
      accessibleOffices = account.offices.filter((o) => !!o.users?.find((u) => u.id === me.id)).map(counts);
    }

    // The list is derived, so it is a new array on every pass and dispatching it
    // unconditionally replaced the reference in the store each time - which
    // churned the current office and, through it, re-fired the project-count
    // aggregates keyed on the office. Recompute freely; only write when the
    // offices genuinely differ, by value, so a rename or a deactivation still
    // lands while a rebuild of the same list does not.
    if (!sameContent(accessibleOffices, state.offices)) {
      dispatch(accountActions.setOffices(accessibleOffices));
    }
  }, [account, me, state.officeAccountId, state.offices]);

  const [inRefresh, setInRefresh] = React.useState(false);

  const refresh = React.useCallback(() => {
    if (!account) return;

    setInRefresh(true);
    refreshAccounts().finally(() => setInRefresh(false));
  }, [account]);

  return [state.offices, refresh, inRefresh];
};

export const useCurrentUserOffice = (): [Office | undefined, (office?: Office | string | null) => void] => {
  const [account] = useCurrentUserAccount();
  const [offices] = useUserOffices();

  const state = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (!offices) return;
    const storedOfficeId = localStorage.getItem(`currentOffice-${account?.id || ''}`);
    let officeToSet: Office | undefined;

    if (storedOfficeId) {
      const foundOffice = offices.find((x) => x.id === storedOfficeId);
      if (foundOffice?.isActive) {
        officeToSet = foundOffice;
      }
    }

    // if (!officeToSet) {
    //   // If no stored active office, try the first active office from the list
    //   officeToSet = offices.find((o) => o.isActive);
    // }

    // Same reason as the office list above: writing back the office that is
    // already current changed its reference and re-fired everything keyed on it.
    if (!sameId(officeToSet, state.currentOffice) || (!officeToSet && state.currentOffice)) {
      dispatch(accountActions.setCurrentOffice(officeToSet));
    }
    if (officeToSet) {
      localStorage.setItem(`currentOffice-${account?.id || ''}`, officeToSet.id);
    } else {
      localStorage.removeItem(`currentOffice-${account?.id || ''}`);
    }
  }, [offices, account]);

  const setCurrentCb = React.useCallback(
    (officeInput?: Office | string | null) => {
      if (!offices) {
        dispatch(accountActions.setCurrentOffice(undefined));
        localStorage.removeItem(`currentOffice-${account?.id || ''}`);
        return;
      }

      let officeToSet: Office | undefined;

      if (officeInput) {
        const id = typeof officeInput === 'string' ? officeInput : officeInput.id;
        const foundOffice = offices.find((x) => x.id === id);
        if (foundOffice?.isActive) {
          officeToSet = foundOffice;
        }
      }

      // if (!officeToSet) {
      //   // If no provided active office or input is null/undefined, try the first active office from the list
      //   officeToSet = offices.find((o) => o.isActive);
      // }

      dispatch(accountActions.setCurrentOffice(officeToSet));
      if (officeToSet) {
        localStorage.setItem(`currentOffice-${account?.id || ''}`, officeToSet.id);
      } else {
        localStorage.removeItem(`currentOffice-${account?.id || ''}`);
      }
    },
    [account, offices],
  );

  return [state.currentOffice, setCurrentCb];
};

export type LaborRateEx = Omit<LaborRate, 'history'> & {
  createdBy?: User;
  updatedBy?: User;
  history?: LaborRateHistoryEx[];
};
export type LaborRateHistoryEx = LaborRateHistory & {
  createdBy?: User;
};

export const useLaborRates = (): [LaborRateEx[] | undefined, () => Promise<void>] => {
  const [account] = useCurrentUserAccount();

  const [rates, setRates] = React.useState<LaborRateEx[] | undefined>(undefined);

  const refresh = React.useCallback(async () => {
    if (!account?.id) return;
    setRates(await getLaborRates(account.id));
  }, [account?.id]);

  React.useEffect(() => {
    if (!account?.id) return;

    const abort = new AbortController();
    getLaborRates(account.id, undefined, undefined, abort).then(async (result) => {
      const rates = result as LaborRateEx[];

      const links = new Set<string>();
      rates.forEach((rate) => {
        if (rate.links?.createdBy) links.add(rate.links.createdBy);
        if (rate.links?.updatedBy) links.add(rate.links.updatedBy);
        rate.history?.forEach((h) => {
          if (h.links?.user) links.add(h.links.user);
        });
      });

      try {
        const linkResult = await getByLinks([...links]);

        rates.forEach((rate) => {
          if (!rate.links) return;

          if (linkResult[rate.links.createdBy]) rate.createdBy = linkResult[rate.links.createdBy] as User;
          if (linkResult[rate.links.updatedBy]) rate.updatedBy = linkResult[rate.links.updatedBy] as User;

          rate.history?.forEach((h) => {
            if (!h.links) return;
            if (linkResult[h.links.user]) h.createdBy = linkResult[h.links.user] as User;
          });
        });
      } catch (err) {
        // DO NOTHING
      } finally {
        setRates(rates);
      }
    });

    return () => {
      abort.abort();
    };
  }, [account?.id]);

  return [rates, refresh];
};

export const useUsers = () => {
  const state = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();

  const getUser = React.useCallback(
    async (id: string | string[]): Promise<User[]> => {
      const ids = Array.isArray(id) ? id : [id];

      const readyUsers = state.users?.filter((u) => ids.indexOf(u.id) >= 0) ?? [];
      const pendingUserIds = ids.filter((id) => !readyUsers.find((u) => u.id === id)).map((id) => id);

      // If no pending users, we are done
      if (pendingUserIds.length <= 0) return readyUsers;

      const result = await getUsers({ ids: pendingUserIds });

      const newUsers = [...(state.users ?? [])];
      result.forEach((u_2) => {
        const idx = newUsers.findIndex((nu) => nu.id === u_2.id);
        if (idx >= 0) newUsers[idx] = u_2;
        else newUsers.push(u_2);
      });

      const final = [...readyUsers, ...result];
      dispatch(accountActions.setUsers(final));

      return final;
    },
    [state.users],
  );

  return {
    users: state.users ?? [],
    getUser,
  };
};

export const useSubscription = () => {
  const [account] = useCurrentUserAccount();

  const [subscription, setSubscription] = React.useState<Subscription | undefined>(undefined);

  React.useEffect(() => {
    if (!account?.id) return;

    const abort = new AbortController();
    getSubscription(account.id, abort).then(setSubscription);

    return () => {
      abort.abort();
    };
    // Keyed on the id, not the account object. The account is rebuilt in the store often enough
    // that depending on its identity re-ran this on renders where nothing about the account had
    // actually changed — and each run calls setSubscription with a freshly deserialised object,
    // which is a new reference and so renders again.
  }, [account?.id]);

  const getLicense = React.useCallback(
    async (userId: string) => {
      if (!account?.id) return;

      try {
        return await getSubscriptionLicense(account.id, userId);
      } catch {
        return undefined;
      }
    },
    [account?.id],
  );

  return {
    subscription,
    getLicense,
  };
};

export const useRights = (officeId?: string, projectId?: string): Rights | undefined => {
  const [account] = useCurrentUserAccount();
  const [accountWithUsers] = useAccountWithUser(projectId);
  const [me] = useMe();

  const [rights, setRights] = React.useState<Rights | undefined>(undefined);
  const buildRights = React.useCallback(() => {
    if (!me || !accountWithUsers) return undefined;

    let userType = UserType.client;

    if (accountWithUsers?.type === AccountType.Provider) {
      userType =
        accountWithUsers.users?.find((u) => u.id === me.id)?.role === ResourceUserRole.Contributor
          ? UserType.expert
          : UserType.provider;
    }

    const rights: Rights = {
      primaryProvider: accountWithUsers?.id === PRIMARY_PROVIDER_ID,
      accountType: accountWithUsers?.type || AccountType.Expert,
      userType: accountWithUsers?.type ? userType : UserType.expert,
      accountId: accountWithUsers?.id,
      id: me.id,
      isGuest: true,
      canListOffices: false,
      offices: [],
      clients: [],
      estimateApps: [],
      projects: [],
      // Set AI prompt management rights for super admins
      canManageAIPrompts: me.systemRole === 'Administration',
    };

    const au = accountWithUsers.users?.find((x) => x.id === me.id);
    if (au) {
      if (accountWithUsers.type === AccountType.Provider) {
        rights.userType = rights.userType === UserType.provider ? UserType.provider : UserType.expert;
        rights.canChat = !rights.primaryProvider;

        // Owners only — a Guest must never manage billing.
        if (account?.type === AccountType.Provider && au.role === ResourceUserRole.Owner) {
          rights.canListAccounts = rights.primaryProvider;
          rights.canListTx = !rights.primaryProvider;
          rights.canViewSubscription = !rights.primaryProvider;
          rights.canManageSubscription = !rights.primaryProvider;
          rights.canCreateOffice = !rights.primaryProvider;
          rights.canListClients = !rights.primaryProvider;
          rights.canEditOffice = !rights.primaryProvider;
          rights.canInviteToOffice = !rights.primaryProvider;
          rights.canListOffices = !rights.primaryProvider;
        }
        if (au.role === ResourceUserRole.Owner) {
          rights.canEditAccount = true;
          rights.canClassifyProjectType = true;
          rights.canCreateTx = true;
          rights.canEditTx = true;
          rights.canDeleteTx = true;

          rights.canListDocs = true;
          rights.canCreateDoc = true;
          rights.canEditDoc = true;
          rights.canDeleteDoc = true;

          rights.canCreateProject = true;

          rights.canInviteAccount = true;
          rights.canInviteClient = true;
          rights.canEditClient = true;
          rights.canDeleteClient = true;

          rights.canViewFees = true;
        }
        if (au.role === ResourceUserRole.Administrator) {
          rights.canInviteAccount = true;
          rights.canInviteClient = true;
          rights.canEditClient = true;
          rights.canDeleteClient = true;

          // rights.canViewFees = true
        }
        if (au.role === ResourceUserRole.Guest) {
          rights.isGuest = true;

          // rights.canViewFees = true
        }
      } else if (accountWithUsers.type === AccountType.Client) {
        rights.userType = UserType.client;
        if (au?.role === ResourceUserRole.Owner || au?.role === ResourceUserRole.Administrator) {
          rights.canListOffices = true;
          rights.canListTx = true;
        }

        // Check if a user is a contributor at account level OR in any office
        // This ensures consistent dashboard restriction regardless of office context
        const isAccountContributor = au?.role === ResourceUserRole.Contributor;
        const isOfficeContributor = accountWithUsers.offices?.some((o) =>
          o.users?.some((u) => u.id === me.id && u.role === ResourceUserRole.Contributor),
        );

        if (isAccountContributor || isOfficeContributor) {
          rights.canViewDashboard = false;
        }

        if (au?.role === ResourceUserRole.Owner) {
          rights.canEditAccount = true;
          rights.canInviteAccount = true;
          rights.canChat = true;
          rights.canCreateProject = true;
          rights.canInviteToProject = true;
          rights.canCreateOffice = true;
          rights.canEditOffice = true;
          rights.canInviteToOffice = true;
          rights.canUploadFile = true;

          // Clients buy AI Hub seats themselves — same roles that can see the AI Hub at all.
          rights.canViewSubscription = true;
          rights.canManageSubscription = true;

          if ((account?.offices?.length || 0) > 1) {
            rights.canDeleteOffice = true;
          }

          rights.canDeleteAccount = true;
        }
        if (au.role === ResourceUserRole.Administrator) {
          rights.canCreateProject = false;
          rights.canInviteToProject = true;
          rights.canUploadFile = true;
          rights.canChat = true;
          rights.canViewSubscription = true;
          rights.canManageSubscription = true;

          if (accountWithUsers.type === AccountType.Client) {
            rights.canEditAccount = true;
            rights.canInviteAccount = true;
            rights.canEditOffice = true;
            rights.canInviteToOffice = true;
            rights.canCreateOffice = true;
          } else {
            rights.canEditAccount = false;
            rights.canInviteAccount = false;
            rights.canEditOffice = false;
            rights.canInviteToOffice = false;
            rights.canCreateOffice = false;
          }

          rights.canInviteAccountExpertOnly = rights.canInviteAccount;
        }
        if (au.role === ResourceUserRole.Contributor && au.providerPosition !== 'Estimator') {
          // Per roles doc §11: a Project Manager can assign staff to projects; an Estimator cannot.
          rights.canInviteToProject = true;
        }
      } else {
        rights.userType = UserType.expert;
      }

      rights.isGuest = au?.role === ResourceUserRole.Guest;
      rights.providerPosition = au?.providerPosition;
    } else if (accountWithUsers.type === AccountType.Client) {
      // User has NO account-level role. DO NOT grant canListOffices here.
      rights.userType = UserType.client;
      // Check if a user is an office-level contributor in any office
      const isOfficeContributor = accountWithUsers.offices?.some((o) =>
        o.users?.some((u) => u.id === me.id && u.role === ResourceUserRole.Contributor),
      );
      if (isOfficeContributor) {
        rights.canViewDashboard = false;
      }
    }

    // Collect office roles for the user
    rights.offices =
      rights.accountType === AccountType.Expert
        ? []
        : accountWithUsers.offices.map((o) => {
            const u = o.users.find((x) => x.id === me.id);

            if (u) {
              rights.isGuest = u.role === ResourceUserRole.Guest;

              // Office-scoped permissions (applies to provider and client offices)
              if (officeId === o.id) {
                if (u.role === ResourceUserRole.Owner) {
                  // "Office Administrator" (formerly Portfolio Manager)
                  rights.canEditOffice = true;
                  rights.canInviteToOffice = true;
                  rights.canCreateProject = true;
                  rights.canListTx = true;
                } else if (u.role === ResourceUserRole.Administrator) {
                  // "Portfolio Manager" (formerly Portfolio Administrator)
                  rights.canEditOffice = false; // Cannot edit office profile
                  rights.canInviteToOffice = true; // Can still invite users
                  rights.canCreateProject = true;
                  rights.canInviteToProject = true;
                } else if (u.role === ResourceUserRole.Contributor) {
                  // "Project Manager"
                  rights.canCreateProject = false; // Cannot create new projects at the office level
                  rights.canEditOffice = false; // Cannot edit office details
                  rights.canInviteToOffice = false; // Cannot invite users to the office
                  rights.canInviteToProject = true; // CAN invite to projects they are part of
                }
              }
            }

            return {
              officeId: o.id,
              role: u?.role || ResourceUserRole.Guest,
              canCreateProject: u?.role === ResourceUserRole.Owner || u?.role === ResourceUserRole.Administrator,
              canDeleteProject: u?.role === ResourceUserRole.Owner,
              canViewDashboard: u?.role !== ResourceUserRole.Contributor, // Contributors cannot view the dashboard
            };
          });

    rights.clients =
      rights.accountType === AccountType.Expert
        ? []
        : accountWithUsers.clients.map((c) => {
            const u = c.users.find((x) => x.id === me.id);

            if (u) {
              rights.isGuest = u.role === ResourceUserRole.Guest;
            }

            return {
              clientId: c.id,
              role: u?.role || ResourceUserRole.Guest,
              canCreateProject: u?.role === ResourceUserRole.Owner || u?.role === ResourceUserRole.Administrator,
              canDeleteProject: u?.role === ResourceUserRole.Owner,
            };
          });

    rights.estimateApps = account?.estimateApps?.map((x) => x.type) || [];

    rights.projects = accountWithUsers.projects.map((p) => ({
      projectId: p.id,
      role: p.users.find((x) => x.id === me.id)?.role || ResourceUserRole.Guest,
      phases: p.phases.map((ph) => ({
        phaseId: ph.id,
        role: ph.users.find((x) => x.id === me.id)?.role || ResourceUserRole.Guest,
        estimates: ph.estimates.map((est) => ({
          estimateId: est.id,
          role: est.users.find((x) => x.id === me.id)?.role || ResourceUserRole.Guest,
        })),
      })),
    }));

    if (rights.accountType !== AccountType.Expert) {
      if (officeId) {
        const ou = accountWithUsers.offices?.find((x) => x.id === officeId)?.users.find((x) => x.id === me.id);
        if (ou) {
          // This section primarily sets top-level rights based on the *current office context*
          // For Project Managers (Contributors) in a Client account, canCreateProject at a general level should be false.
          if (accountWithUsers.type === AccountType.Client && ou.role === ResourceUserRole.Contributor) {
            rights.canCreateProject = false;
          } else {
            rights.canCreateProject =
              ou.role === ResourceUserRole.Owner ||
              ou.role === ResourceUserRole.Administrator ||
              ou.role === ResourceUserRole.Contributor;
          }
          rights.canEditOffice = ou.role === ResourceUserRole.Owner; // Only Office Owner can edit
          rights.canInviteToOffice = ou.role === ResourceUserRole.Owner || ou.role === ResourceUserRole.Administrator; // Owner or Admin can invite

          rights.isGuest = ou.role === ResourceUserRole.Guest;

          rights.canDeleteOffice =
            (ou.role === ResourceUserRole.Owner || ou.role === ResourceUserRole.Administrator) &&
            (accountWithUsers.offices.length || 0) > 1;
        }
      } else {
        // This section determines a general canCreateProject if NO specific officeId is in context.
        // For a client account, if a user is a Contributor in *any* office, this was setting canCreateProject to true.
        // We need to ensure this doesn't override the more specific "false" for Contributors.
        // However, the logic here is for when no officeId is present, so it looks for *any* office where the user *could* create a project.
        // Since Contributors *cannot* create projects at the office level, this part should effectively not grant them canCreateProject.
        // const o = accountWithUsers.offices?.filter(
        //   (ox) => !!ox.users?.find((u) => u.id === me?.id && (u.role === ResourceUserRole.Owner || u.role === ResourceUserRole.Administrator /* Contributor removed here */))
        // )
        // if (o?.length) rights.canCreateProject = true
        // If the user is a contributor in some office, but not an Owner/Admin in any,
        // and no specific officeId is set, rights.canCreateProject should remain false or be determined by account-level roles.
        // The current logic might still set it to true if they are a Contributor in *any* office due to the line above.
        // Let's refine the general canCreateProject if no officeId, considering the account type.
        if (accountWithUsers.type === AccountType.Client) {
          const userAccountRole = accountWithUsers.users?.find((u) => u.id === me.id)?.role;
          if (userAccountRole === ResourceUserRole.Owner || userAccountRole === ResourceUserRole.Administrator) {
            // Account Owner/Admin can create projects
            rights.canCreateProject = true;
          } else {
            // If not an account Owner/Admin, and no specific office context allows it, then no.
            // This also covers Contributors at the account level if that's possible, or users with only office roles.
            const officeRolesAllowCreate = accountWithUsers.offices?.some(
              (ox) =>
                !!ox.users?.find(
                  (u) =>
                    u.id === me?.id && (u.role === ResourceUserRole.Owner || u.role === ResourceUserRole.Administrator),
                ),
            );
            rights.canCreateProject = !!officeRolesAllowCreate;
          }
        }
      }
    }

    // Grant project-specific rights if projectId is provided
    const projectUser = projectId
      ? accountWithUsers.projects.find((p) => p.id === projectId)?.users.find((u) => u.id === me.id)
      : undefined;
    if (projectId) {
      if (accountWithUsers.type === AccountType.Client && projectUser?.role === ResourceUserRole.Contributor) {
        rights.canUploadFile = true;
      }
    }

    // Fee rights for the client: a project decision, decided apart from software purchase (see
    // isClientFeeDecider). The client's project Owner/Administrator, or its account Owner/Administrator.
    if (isClientFeeDecider(accountWithUsers.type, au, projectUser)) {
      rights.canViewFees = true;
      rights.canDecideFee = true;
    }

    // Logic for canManageSingleOfficeInSettings
    if (rights.accountType === AccountType.Client && !rights.canListOffices && accountWithUsers.offices) {
      for (const office of accountWithUsers.offices) {
        const userInOffice = office.users.find((u) => u.id === me.id);
        if (
          userInOffice &&
          (userInOffice.role === ResourceUserRole.Owner || userInOffice.role === ResourceUserRole.Administrator)
        ) {
          rights.canManageSingleOfficeInSettings = true;
          rights.manageableOfficeIdInSettings = office.id;
          break; // Found the first manageable office
        }
      }
    }

    // AI Hub launcher visibility. Superset of chat access plus experts, whose role
    // is AI Hub QA/QC. Refine later to derive from the account's real AI Hub
    // entitlement (subscription / estimateApps) once that signal is available.
    rights.canUseAiHub = !!rights.canChat || rights.userType === UserType.expert;

    /**
     * Persona overrides, composed over the answer the tier and scope logic above
     * already reached.
     *
     * Applied here, last and in one place, so none of the audited branches move.
     * An override only ever widens: a persona can say "this tier may also do
     * this", never "may no longer", because taking a capability away belongs in
     * the rights model where it can be audited against the roles document.
     *
     * The one override today is a contractor's Production Manager - an
     * Administrator who runs the work, and work starts with a project. On every
     * other account type an Administrator deliberately cannot start one, and
     * that is untouched.
     */
    const overrides = PERSONA_REGISTRY[resolvePersona(rights, account)].capabilityOverrides;
    if (overrides?.canCreateProjectTiers?.length) {
      const tier = accountWithUsers.users.find((u) => u.id === me.id)?.role;
      if (tier && overrides.canCreateProjectTiers.includes(tier)) rights.canCreateProject = true;
    }

    return rights;
  }, [me, account, accountWithUsers, officeId, projectId]);

  // Rebuilt when the identities it is derived from change, and not otherwise.
  //
  // This effect used to list `rights` among its own dependencies, behind a guard that compared
  // `rights.accountId` to `account?.id`. buildRights sets that field from `accountWithUsers.id`,
  // so the guard only ever passed while those two agreed — and they disagree for as long as the
  // account is switching, and permanently whenever the accountWithUsers fetch fails and leaves a
  // stale or absent value behind.
  //
  // With the guard failing, every run called setRights with a freshly built object; that changed
  // `rights`; and the changed `rights` re-ran the effect. A render loop with no terminal state,
  // which is what kept re-mounting the wizard step underneath it and re-firing its licence read —
  // the repeating 404 was the symptom, not the cause.
  //
  // Keyed on ids rather than on the objects: the redux slices hand back new references routinely,
  // and the ids are what actually decide the answer. Deliberately does not depend on buildRights,
  // whose identity turns over with those same objects.
  const rightsKey = [me?.id, account?.id, accountWithUsers?.id, officeId, projectId].join('|');

  React.useEffect(() => {
    setRights(buildRights());
  }, [rightsKey]);

  return rights;
};
